import classNames from 'classnames';
import Head from 'next/head';
import {useRouter} from 'next/router';
import React, {useEffect, useState} from 'react';

import {HeadSEO} from '@udemy/head-seo';
import {LayoutBody, LayoutContainer, LayoutMenu} from '@udemy/react-menu-layout-components';
import {Loader} from '@udemy/react-reveal-components';

import {getLayout} from 'src/components/layout';
import {
    LegalPagesNavAsDropdown,
    LegalPagesNavAsSideBar,
} from 'src/components/legal-pages-nav/legal-pages-nav.react-component';
import {getRuntimeConfig} from 'src/pages/api/config.api';
import {StaticPage} from 'src/types/cms-types';
import {PageProps} from 'src/types/page-props';

import {fetchPageContentFromCMS} from '../../lib/fetch-page-content';
import NotFoundPage from '../../pages/404.page';
import styles from './terms.module.less';
import {TermsPage} from './terms.react-component';

export type TermsPageProps = Pick<PageProps, 'locale'>;

export function TermsPageFeature({locale = 'en'}: TermsPageProps) {
    const router = useRouter();
    const [pageData, setPageData] = useState<StaticPage>({} as StaticPage);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const loadContent = async (currentSlug: string) => {
            try {
                setLoading(true);

                // Check for preview_mode query parameter
                const urlParams = new URLSearchParams(router.asPath.split('?')[1] || '');
                const isPreviewEnabled = urlParams.get('preview_mode') === 'true';

                const response = await fetchPageContentFromCMS({
                    locale,
                    slug: currentSlug,
                    isPreviewEnabled,
                });
                setPageData(response);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Response data is empty');
                setPageData({} as StaticPage);
            } finally {
                setLoading(false);
            }
        };

        loadContent(router.asPath.split('?')[0].split('#')[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.asPath]);

    // Scroll to anchor after content loads
    useEffect(() => {
        if (!loading && typeof window !== 'undefined') {
            const hash = window.location.hash;
            if (hash) {
                setTimeout(() => {
                    requestAnimationFrame(() => {
                        const el = document.getElementById(hash.slice(1));
                        if (el) {
                            el.scrollIntoView({behavior: 'smooth'});
                        }
                    });
                }, 100);
            }
        }
    }, [loading]);

    return (
        <>
            <HeadSEO
                ogTitle={pageData?.metaTitle}
                description={pageData?.metaDescription}
                ogDescription={pageData?.metaDescription}
                ogType="some_opengraph_type"
                ogLocale={process.env.I18N_PATH_TO_BUNDLE_MAP[locale]}
                // TODO: Add canonical url later
                canonicalUrl={`${getRuntimeConfig('CMS_DISPATCHER_URL')}${pageData?.slug}`}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            <div>
                <div className={styles['legal-pages-nav--mobile']}>
                    <LegalPagesNavAsDropdown />
                </div>
                <LayoutContainer>
                    <LayoutMenu>
                        <LegalPagesNavAsSideBar />
                    </LayoutMenu>
                    <LayoutBody>
                        <div className={classNames('documentation__main', styles['content'])}>
                            {loading ? (
                                <Loader
                                    block={true}
                                    size="xlarge"
                                    className={styles.loader}
                                    data-testid="loader"
                                />
                            ) : pageData?.contents?.[0].content === undefined ? (
                                NotFoundPage()
                            ) : (
                                <TermsPage content={pageData?.contents?.[0].content} />
                            )}
                        </div>
                    </LayoutBody>
                </LayoutContainer>
            </div>
        </>
    );
}

TermsPageFeature.getLayout = getLayout;
